/**
 * Format an AI-generated funeral service order as plain-text suitable
 * for sharing via WhatsApp, email, SMS, or clipboard paste. Keeps the
 * structure legible without depending on markdown or HTML.
 */
export function formatServiceOrderText(result, personName) {
  if (!result) return "";
  const lines = [];
  const heading = personName ? `Service Order — In memory of ${personName}` : "Service Order";
  lines.push(heading);
  lines.push("=".repeat(Math.min(heading.length, 60)));
  lines.push("");

  const section = (title, rows, render) => {
    if (!rows?.length) return;
    lines.push(title.toUpperCase());
    rows.forEach((r) => lines.push(render(r)));
    lines.push("");
  };

  section("Order of service", result.order_of_service, (s) => `  ${s.step}. ${s.title}${s.description ? ` — ${s.description}` : ""}`);
  section("Hymns / songs",   result.hymns,            (h) => `  • ${h.title}${h.note ? ` (${h.note})` : ""}`);
  section("Readings",        result.readings,         (r) => `  • ${r.title}${r.note ? ` (${r.note})` : ""}`);
  section("Speakers",        result.speakers,         (s) => `  • ${s.role}${s.note ? ` — ${s.note}` : ""}`);
  section("Pallbearers",     result.pallbearers,      (p) => `  • ${p.role}${p.note ? ` — ${p.note}` : ""}`);
  section("Honorary pallbearers", result.honorary_pallbearers, (p) => `  • ${p.role}${p.note ? ` — ${p.note}` : ""}`);

  if (result.reception) {
    lines.push("RECEPTION");
    lines.push(`  ${result.reception}`);
    lines.push("");
  }
  if (result.flowers_and_donations) {
    lines.push("FLOWERS & DONATIONS");
    lines.push(`  ${result.flowers_and_donations}`);
    lines.push("");
  }
  if (result.closing_prayer) {
    lines.push("CLOSING");
    lines.push(`  ${result.closing_prayer}`);
    lines.push("");
  }

  lines.push("— Drafted with Social Pulse · socialpulse.com/celebrate");
  return lines.join("\n");
}


/**
 * Encode a plain-text ceremony program into share-target URLs. Returns
 * `{whatsapp, email, sms}` — each is a `href`-ready deep link that
 * opens the platform's compose window pre-filled with the text.
 */
export function buildShareLinks(text, subject = "Order of service") {
  const enc = encodeURIComponent(text);
  return {
    whatsapp: `https://wa.me/?text=${enc}`,
    email:    `mailto:?subject=${encodeURIComponent(subject)}&body=${enc}`,
    sms:      `sms:?body=${enc}`,
  };
}
